import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Search, Plus, Check } from "lucide-react";
import useSwipeToClose from "../../hooks/useSwipeToClose";
import { GROCERY_HOST } from "../../services/apiConfig";
import {
  getAuthHeaders,
  getLocalizedProductName,
  getSearchDocImage,
  getSearchDocPrice,
  getSearchDocAmountLabel,
} from "./utils/productListUtils";

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

/**
 * AddProductDrawer — bottom sheet that lets a user search the grocery catalog
 * (via the existing Typesense multi-search endpoint) and add products to their
 * personal "My products" list. Selecting a product does NOT close the drawer;
 * the user closes it explicitly with the bottom CTA.
 *
 * Props:
 *   - isOpen: boolean
 *   - onClose: () => void
 *   - onToggleProduct: (doc) => void   // add/remove a product
 *   - selectedIds: string[]            // ids already in "My products"
 *   - currencySymbol: string
 */
function AddProductDrawer({
  isOpen,
  onClose,
  onToggleProduct,
  selectedIds = [],
  currencySymbol = "€",
}) {
  const { t, i18n } = useTranslation("mealplan");
  const { containerRef, dragOffset, shouldRender, isVisible, DragHandle } =
    useSwipeToClose(onClose, isOpen, { disableSwipeToClose: true });

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  // iOS virtual-keyboard inset: amount by which the keyboard overlaps the
  // layout viewport. Used to lift the bottom CTA above the keyboard. 0 on
  // platforms that don't expose visualViewport (e.g. desktop).
  const [keyboardInset, setKeyboardInset] = useState(0);

  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  // Reset state whenever the drawer is (re)opened.
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setError(null);
      setHasSearched(false);
    }
  }, [isOpen]);

  // Track the iOS virtual keyboard so we can lift the bottom CTA above it.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (typeof window === "undefined" || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const update = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(inset);
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [isOpen]);

  const runSearch = useCallback(
    async (term) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      setError(null);

      // Search across every indexed translation so users can type product
      // names in any supported language, not just English/Spanish. The current
      // UI language is weighted highest, then English, then the rest.
      const SUPPORTED_LANGS = ["en", "es", "ca", "fr", "it", "ro", "ru", "uk"];
      const uiLang = (i18n.language || "en").split("-")[0];
      const orderedLangs = [
        ...(SUPPORTED_LANGS.includes(uiLang) ? [uiLang] : []),
        ...SUPPORTED_LANGS.filter((l) => l !== uiLang),
      ];
      const langFields = orderedLangs.map((l) => `lang.${l}`);
      const queryByFields = ["keywords", "displayName", "name", ...langFields];
      // keywords:30, displayName:3, name:3, current lang:20, remaining langs:5
      const queryByWeights = [
        "30",
        "3",
        "3",
        ...orderedLangs.map((l, idx) => (idx === 0 && l === uiLang ? "20" : "5")),
      ];

      try {
        const response = await fetch(`${GROCERY_HOST}/api/products/search`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            searches: [
              {
                collection: "products",
                q: term,
                query_by: queryByFields.join(","),
                query_by_weights: queryByWeights.join(","),
                per_page: 20,
                prioritize_exact_match: true,
              },
            ],
          }),
        });

        if (!response.ok) {
          throw new Error(`Search failed (${response.status})`);
        }

        const data = await response.json();
        // Ignore stale responses.
        if (requestId !== requestIdRef.current) return;

        const hits = data?.results?.[0]?.hits || [];
        const docs = hits.map((h) => h.document).filter(Boolean);
        setResults(docs);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        console.error("[AddProductDrawer] search error:", err);
        setError(t("grocery.myProducts.searchError"));
        setResults([]);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [t, i18n.language]
  );

  // Debounced search on query change.
  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      requestIdRef.current++; // cancel any in-flight request
      setResults([]);
      setLoading(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    setHasSearched(true);
    const handle = setTimeout(() => runSearch(term), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, runSearch]);

  if (!shouldRender) return null;

  const selectedSet = new Set(selectedIds);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-10 h-10 border-[3px] border-[#E8E6E3] border-t-[#65635D] rounded-full animate-spin" />
        </div>
      );
    }

    if (error) {
      return (
        <p className="text-center text-[14px] text-[#991B1B] py-10">{error}</p>
      );
    }

    if (!hasSearched) {
      return (
        <p className="text-center text-[14px] text-[#65635D] py-10">
          {t("grocery.myProducts.searchHint")}
        </p>
      );
    }

    if (results.length === 0) {
      return (
        <p className="text-center text-[14px] text-[#65635D] py-10">
          {t("grocery.myProducts.noResults")}
        </p>
      );
    }

    return (
      <div className="flex flex-col">
        {results.map((doc) => {
          const id = doc.id;
          const isAdded = selectedSet.has(id);
          const name = getLocalizedProductName(doc, i18n.language);
          const image = getSearchDocImage(doc);
          const amountLabel = getSearchDocAmountLabel(doc, t);
          const price = getSearchDocPrice(doc);
          return (
            <button
              key={id}
              type="button"
              onClick={() => onToggleProduct && onToggleProduct(doc)}
              className="flex items-center gap-3 py-3 border-b border-[#E8E6E3] text-left transition-colors md:hover:bg-[#f3f3f2] rounded-[8px] px-2 -mx-2"
            >
              <div className="flex-shrink-0 w-14 h-14 rounded-[8px] overflow-hidden bg-gray-100">
                <img
                  src={image}
                  alt={name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = "/assets/products/no-image.jpg";
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium leading-[20px] text-[#0D0C07] line-clamp-2">
                  {name}
                </p>
                {amountLabel && (
                  <p className="text-[12px] leading-[16px] text-[#65635D] mt-0.5">
                    {amountLabel}
                  </p>
                )}
              </div>
              {price != null && (
                <span className="flex-shrink-0 text-[14px] font-semibold text-[#0D0C07] whitespace-nowrap">
                  {currencySymbol}
                  {price.toFixed(2)}
                </span>
              )}
              <span
                className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isAdded
                    ? "bg-[#060] text-white"
                    : "bg-[#F5F3F0] text-[#0D0C07]"
                }`}
              >
                {isAdded ? <Check size={18} strokeWidth={2.5} /> : <Plus size={18} strokeWidth={2.5} />}
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 z-[200] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        zIndex: 99999,
      }}
      onClick={onClose}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#f8f8f7] shadow-lg drawer-content overflow-hidden rounded-none md:rounded-[12px_12px_0px_0px] md:max-w-5xl md:mx-auto h-[100dvh] md:h-auto"
        style={{
          maxHeight: "100dvh",
          transform: isVisible
            ? `translateY(${Math.max(0, dragOffset)}px)`
            : "translateY(100%)",
          transition:
            dragOffset === 0
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />

        {/* Close (X) button */}
        <button
          onClick={onClose}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-9 h-9 rounded-[12px] transition-colors z-20"
          aria-label={t("grocery.myProducts.close")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D" />
          </svg>
        </button>

        {/* Header: title + search input. Sticky so it stays pinned to the
            top of the visible viewport even when iOS scrolls the page to
            reveal a focused input above the virtual keyboard. */}
        <div
          className="flex-shrink-0 px-6 pt-8 pb-3 bg-[#f8f8f7] sticky top-0 z-10"
          style={{ paddingTop: "max(2rem, env(safe-area-inset-top, 0px))" }}
        >
          <h2 className="text-[18px] leading-[28px] font-bold text-[#0d0c07] pb-3">
            {t("grocery.myProducts.title")}
          </h2>
          <div className="flex items-center gap-2 bg-white border-[0.5px] border-[#D7D7D5] rounded-[12px] px-3 h-[48px]">
            <Search size={20} strokeWidth={1.75} className="text-[#65635D] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => {
                // iOS sometimes scrolls the input under the keyboard. Nudge
                // it back into view once the keyboard animation has settled.
                setTimeout(() => {
                  inputRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
                }, 300);
              }}
              placeholder={t("grocery.myProducts.searchPlaceholder")}
              className="flex-1 min-w-0 bg-transparent border-0 outline-none text-[16px] leading-[24px] text-[#0D0C07] placeholder:text-[#9b9a94]"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
          </div>
        </div>

        {/* Scrollable results */}
        <div
          className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden px-6 pb-6 md:pb-0"
          style={{ paddingBottom: keyboardInset ? `${keyboardInset + 24}px` : undefined }}
        >
          {renderBody()}
        </div>

        {/* Bottom CTA — desktop only. On mobile the corner X is the close affordance. */}
        <div
          className="hidden md:flex fixed bottom-0 left-0 right-0 pt-2 pb-5 px-0 justify-center bg-white z-10 rounded-[12px_12px_0px_0px] shadow-[0px_0px_1px_0px_rgba(13,12,7,0.12),_0px_-4px_24px_0px_rgba(13,12,7,0.12),_1px_-2px_8px_0px_rgba(13,12,7,0.08)]"
          style={{ transform: `translateY(-${keyboardInset}px)`, transition: "transform 200ms ease-out" }}
        >
          <div className="w-full max-w-5xl px-6 flex items-center justify-between">
            <button
              type="button"
              className="inline-flex justify-center items-center gap-1 h-[56px] px-6 py-[14px] rounded-[12px] border-[0.5px] border-[#060] bg-[#060] text-white text-center text-[15.2px] font-semibold leading-[28px] transition-colors md:hover:border-[#090] md:hover:bg-[#090] w-full"
              onClick={onClose}
            >
              {t("grocery.myProducts.close")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AddProductDrawer;
